import { UserManager, User } from "oidc-client-ts";
import { getOidcSettings, isOidcConfigured } from "@/core/config/OidcConfig";

let userManager: UserManager | null = null;

const OidcService = {
  init(): void {
    if (!isOidcConfigured()) return;
    if (userManager) return;
    userManager = new UserManager(getOidcSettings());
  },

  getUserManager(): UserManager | null {
    return userManager;
  },

  async signinRedirect(codeArk: string, returnPath?: string): Promise<void> {
    if (!userManager) return;
    const extraQueryParams: Record<string, string> = {};
    // If Zitadel org ID is available, pass it to scope the login
    const oidcOrgId = window.sessionStorage.getItem("oidc_org_id");
    if (oidcOrgId) {
      extraQueryParams["urn:zitadel:iam:org:id"] = oidcOrgId;
    }
    await userManager.signinRedirect({
      state: { codeArk, returnPath: returnPath || "/board" },
      extraQueryParams,
    });
  },

  async signinRedirectCallback(): Promise<User> {
    if (!userManager) throw new Error("OidcService not initialized");
    return userManager.signinCallback() as Promise<User>;
  },

  async getUser(): Promise<User | null> {
    if (!userManager) return null;
    return userManager.getUser();
  },

  async getAccessToken(): Promise<string | null> {
    if (!userManager) return null;
    const user = await userManager.getUser();
    if (user && !user.expired) {
      return user.access_token;
    }
    return null;
  },

  async isAuthenticated(): Promise<boolean> {
    if (!userManager) return false;
    const user = await userManager.getUser();
    return !!user && !user.expired;
  },

  async signoutRedirect(): Promise<void> {
    if (!userManager) return;
    await userManager.signoutRedirect();
  },

  async signinSilent(): Promise<User | null> {
    if (!userManager) return null;
    return userManager.signinSilent();
  },

  async removeUser(): Promise<void> {
    if (!userManager) return;
    await userManager.removeUser();
  },
};

export default OidcService;
